/**
 * Fetches the dataset bundle from /api/dataset.
 *
 * The endpoint — not a static file — is what makes per-user scoping possible later:
 * today it returns the whole bundle to every signed-in FCC user, but when scoping
 * arrives it becomes a change to one function body rather than to this client or to
 * the measure layer.
 *
 * NOTE: Azure Static Web Apps overwrites the `Authorization` header on its way to the
 * backend, so the access token travels in `X-Id-Token` instead. This is not optional
 * and is not a workaround we can drop — SWA strips it silently and you get a 401 with
 * no clue why.
 */
import type { Dataset } from './types'
import * as cache from './cache'
import { hydrate } from './hydrate'
import { viewAsFailure, withViewAs } from './viewAs'
import { apiFetch, TOKEN_HEADER } from '@/auth/apiFetch'

export { TOKEN_HEADER }

export interface LoadResult {
  dataset: Dataset
  fromCache: boolean
}

/**
 * Where the bundle comes from. Always the API in a normal build; a demo build points
 * this at a relative file so the app can be published somewhere with no backend.
 */
const DATASET_URL = (import.meta.env.VITE_DATASET_URL as string | undefined) ?? '/api/dataset'

export async function loadDataset(accessToken: string | null): Promise<LoadResult> {
  const cached = await cache.read()

  // While viewing as someone, the header asks the API to scope as them (viewAs.ts), and
  // the cache entry is theirs — read and written under its own key.
  const headers = withViewAs({ Accept: 'application/json' })
  if (accessToken) headers[TOKEN_HEADER] = accessToken
  // Revalidate on the server's ETag, NOT on the bundle version. The two differ: the
  // ETag covers the caller's scope as well as the build, so a user whose grants or
  // roles changed gets a fresh 200 instead of a 304 confirming the slice they had
  // before the change. An entry written by an older build carries no etag — treat that
  // as no validator and refetch, which is the safe direction.
  if (cached?.etag) headers['If-None-Match'] = cached.etag

  const res = await apiFetch(DATASET_URL, { headers })

  // `hydrate` on every path, including cache hits: a dataset cached by an earlier
  // build predates the derived fields, and it is idempotent, so this is the one
  // place that can guarantee no raw bundle reaches a measure.
  if (res.status === 304 && cached) {
    return { dataset: hydrate(cached.dataset), fromCache: true }
  }

  if (!res.ok) {
    const reason = await viewAsFailure(res)
    throw new Error(reason ?? `Dataset request failed: ${res.status} ${res.statusText}`)
  }

  const dataset = hydrate((await res.json()) as Dataset)

  // A 200 means the server decided our copy was not current, so the body it just sent
  // IS the answer. There used to be a `cached.version === dataset.version` shortcut
  // here that returned the CACHED dataset instead — the same scope-blind equality that
  // made the 304 path serve a stale slice, just on the path where we already hold the
  // correct one.
  await cache.write(dataset, res.headers.get('ETag') ?? '')
  return { dataset, fromCache: false }
}
